import React from 'react';
import clsx from 'clsx';
import { useBrandTheme } from '../brand-theme';
import type { StepIndicatorProps, BookingStepV4 } from '../types';

/**
 * Horizontal step indicator with progress pills.
 * Shows current step, completed steps, and allows navigation to completed steps.
 * 
 * Uses brand theme colors from context for progress states.
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
}) => {
  const { theme } = useBrandTheme();
  return (
    <nav aria-label="Booking progress" className="w-full">
      {/* Desktop: horizontal pills with labels */}
      <ol className="hidden md:flex items-center justify-center">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const isPast = isStepBefore(step.id, currentStep, steps.map((s) => s.id));
          const canClick = isCompleted || isPast;

          return (
            <li key={step.id} className="flex items-center">
              {/* Connector line - dotted style */}
              {index > 0 && (
                <div
                  className={clsx(
                    'w-12 lg:w-16 mx-2 border-b-2 border-dotted',
                    'transition-colors duration-200',
                    isPast || isCompleted ? theme.accentBorder : 'border-gray-300',
                  )}
                />
              )}
              <button
                type="button"
                onClick={() => canClick && onStepClick?.(step.id)}
                disabled={!canClick && !isCurrent}
                className={clsx(
                  'flex items-center gap-2.5 px-4 py-2.5 rounded-md',
                  'text-sm font-serif transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2',
                  theme.ctaRing,
                  {
                    // Current step - highlighted with brand accent
                    [`${theme.accentBg} text-white shadow-md-tent`]: isCurrent,
                    // Completed step - clickable
                    [`${theme.accentBgLight} ${theme.accentText} hover:opacity-80 cursor-pointer border ${theme.accentBorder}`]:
                      isCompleted && !isCurrent,
                    // Future step - disabled
                    'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200':
                      !isCompleted && !isCurrent,
                  },
                )}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span
                  className={clsx(
                    'flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold font-mono',
                    'transition-colors duration-200',
                    {
                      [`bg-white ${theme.accentText}`]: isCurrent,
                      [`${theme.accentBg} text-white`]: isCompleted && !isCurrent,
                      'bg-gray-200 text-gray-500': !isCompleted && !isCurrent,
                    },
                  )}
                >
                  {isCompleted && !isCurrent ? (
                    <CheckIcon className="w-3.5 h-3.5" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="hidden lg:inline whitespace-nowrap uppercase tracking-wider text-xs">
                  {step.label}
                </span>
                <span className="lg:hidden whitespace-nowrap uppercase tracking-wider text-xs">
                  {step.shortLabel || step.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Mobile: compact pills */}
      <ol className="flex md:hidden items-center justify-center gap-1 py-2">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const isPast = isStepBefore(step.id, currentStep, steps.map((s) => s.id));
          const canClick = isCompleted || isPast;

          return (
            <li key={step.id} className="flex items-center">
              {/* Connector line - dotted style */}
              {index > 0 && (
                <div
                  className={clsx(
                    'w-6 mx-1 border-b-2 border-dotted',
                    'transition-colors duration-200',
                    isPast || isCompleted ? theme.accentBorder : 'border-gray-300',
                  )}
                />
              )}
              <button
                type="button"
                onClick={() => canClick && onStepClick?.(step.id)}
                disabled={!canClick && !isCurrent}
                className={clsx(
                  'flex items-center justify-center w-9 h-9 rounded-md',
                  'text-sm font-mono font-bold transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-offset-1',
                  theme.ctaRing,
                  {
                    [`${theme.accentBg} text-white shadow-md-tent`]: isCurrent,
                    [`${theme.accentBgLight} ${theme.accentText} border ${theme.accentBorder}`]: isCompleted && !isCurrent,
                    'bg-gray-100 text-gray-400 border border-gray-200': !isCompleted && !isCurrent,
                  },
                )}
                aria-label={step.label}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {isCompleted && !isCurrent ? (
                  <CheckIcon className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {/* Mobile: current step label */}
      <div className="md:hidden text-center mt-2">
        <span className="text-sm font-serif font-medium text-gray-700 uppercase tracking-wider">
          {steps.find((s) => s.id === currentStep)?.label}
        </span>
      </div>
    </nav>
  );
};

/**
 * Check if stepA comes before stepB in the step order
 */
function isStepBefore(
  stepA: BookingStepV4,
  stepB: BookingStepV4,
  stepOrder: BookingStepV4[],
): boolean {
  const indexA = stepOrder.indexOf(stepA);
  const indexB = stepOrder.indexOf(stepB);
  return indexA < indexB;
}

/**
 * Check icon component
 */
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

export default StepIndicator;
